import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    Select, MenuItem, FormControl, InputLabel, Typography, Chip, IconButton,
    CircularProgress, Alert, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useFirebase } from '../../../context/FirebaseContext';
import { getAlphaCodes, getAlphaWaves, generateAlphaCodes, deactivateAlphaCode } from '../../../api/alpha';
import logger from '../../../utils/logger';

const CodesManagement = ({ onUpdate }) => {
    const { auth } = useFirebase();
    const [codes, setCodes] = useState([]);
    const [waves, setWaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedWaveFilter, setSelectedWaveFilter] = useState('');
    const [formData, setFormData] = useState({
        waveId: '',
        count: 10,
        type: 'admin',
        maxUses: 1,
        expiresAt: ''
    });
    const [copiedCode, setCopiedCode] = useState(null);

    const loadWaves = useCallback(async () => {
        try {
            const response = await new Promise((resolve) => {
                getAlphaWaves(auth, resolve);
            });
            if (Array.isArray(response)) {
                setWaves(response);
            } else if (response.data) {
                setWaves(response.data);
            }
        } catch (err) {
            logger.error('Error loading waves:', err);
        }
    }, [auth]);

    const loadCodes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await new Promise((resolve) => {
                getAlphaCodes(auth, resolve, selectedWaveFilter || null);
            });
            if (response.error) {
                setError(response.error);
            } else if (Array.isArray(response)) {
                setCodes(response);
            } else if (response.data) {
                setCodes(response.data);
            }
        } catch (err) {
            logger.error('Error loading codes:', err);
            setError('Failed to load codes');
        } finally {
            setLoading(false);
        }
    }, [auth, selectedWaveFilter]);

    useEffect(() => {
        loadWaves();
    }, [loadWaves]);

    useEffect(() => {
        loadCodes();
    }, [loadCodes]);

    const handleGenerateCodes = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await new Promise((resolve) => {
                generateAlphaCodes(auth, resolve, {
                    waveId: formData.waveId,
                    count: formData.count,
                    type: formData.type,
                    maxUses: formData.maxUses,
                    expiresAt: formData.expiresAt || null
                });
            });
            if (response?.error) {
                setError(typeof response.error === 'string' ? response.error : 'Failed to generate codes');
            } else {
                setDialogOpen(false);
                await loadCodes();
                onUpdate?.();
            }
        } catch (err) {
            logger.error('Error generating codes:', err);
            setError('Failed to generate codes');
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async (codeId) => {
        if (!window.confirm('Are you sure you want to deactivate this code?')) return;

        try {
            setLoading(true);
            await new Promise((resolve) => {
                deactivateAlphaCode(auth, resolve, codeId);
            });
            await loadCodes();
            onUpdate?.();
        } catch (err) {
            logger.error('Error deactivating code:', err);
            setError('Failed to deactivate code');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedCode(code);
            setTimeout(() => setCopiedCode(null), 2000);
        } catch (err) {
            logger.error('Failed to copy code:', err);
        }
    };

    const getStatusColor = (code) => {
        if (!code.isActive) return 'default';
        if (code.expired) return 'error';
        if (code.currentUses >= code.maxUses) return 'warning';
        return 'success';
    };

    const getStatusLabel = (code) => {
        if (!code.isActive) return 'Inactive';
        if (code.expired) return 'Expired';
        if (code.currentUses >= code.maxUses) return 'Used';
        return 'Active';
    };

    const formatDate = (dateArray) => {
        if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
        const [year, month, day] = dateArray;
        return new Date(year, month - 1, day).toLocaleDateString();
    };

    const getWaveName = (waveId) => {
        const wave = waves.find(w => w.id === waveId);
        return wave ? wave.name : 'Unknown';
    };

    if (loading && codes.length === 0 && waves.length === 0) {
        return (
            <Box className="alpha-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="codes-management">
            <Box className="alpha-section-header">
                <Typography variant="h6">Invite Codes</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Wave</InputLabel>
                        <Select
                            value={selectedWaveFilter}
                            onChange={(e) => setSelectedWaveFilter(e.target.value)}
                            label="Filter by Wave"
                        >
                            <MenuItem value="">All Waves</MenuItem>
                            {waves.map((wave) => (
                                <MenuItem key={wave.id} value={wave.id}>
                                    {wave.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setDialogOpen(true)}
                        size="small"
                    >
                        Generate Codes
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Code</TableCell>
                            <TableCell>Wave</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Uses</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {codes.map((code) => (
                            <TableRow key={code.id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {code.code}
                                        </Typography>
                                        <Tooltip title={copiedCode === code.code ? 'Copied!' : 'Copy'}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCopyCode(code.code)}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell>{getWaveName(code.waveId)}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={code.type}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    {code.currentUses || 0} / {code.maxUses}
                                </TableCell>
                                <TableCell>{formatDate(code.expiresAt)}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={getStatusLabel(code)}
                                        color={getStatusColor(code)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip title="Deactivate">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeactivate(code.id)}
                                            disabled={!code.isActive}
                                            color="error"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {codes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        No invite codes generated yet
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Generate Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Invite Codes</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Wave</InputLabel>
                            <Select
                                value={formData.waveId}
                                onChange={(e) => setFormData({ ...formData, waveId: e.target.value })}
                                label="Wave"
                            >
                                {waves.filter(w => w.status === 'active').map((wave) => (
                                    <MenuItem key={wave.id} value={wave.id}>
                                        {wave.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Number of Codes"
                            type="number"
                            value={formData.count}
                            onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) })}
                            fullWidth
                            inputProps={{ min: 1, max: 100 }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                label="Type"
                            >
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="vip">VIP</MenuItem>
                                <MenuItem value="referral">Referral</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Max Uses per Code"
                            type="number"
                            value={formData.maxUses}
                            onChange={(e) => setFormData({ ...formData, maxUses: Math.max(1, parseInt(e.target.value) || 1) })}
                            fullWidth
                            inputProps={{ min: 1 }}
                            helperText="Number of times each code can be redeemed"
                        />
                        <TextField
                            label="Expiration Date"
                            type="date"
                            value={formData.expiresAt}
                            onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave empty for no expiration"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerateCodes}
                        disabled={!formData.waveId || formData.count < 1}
                    >
                        Generate
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CodesManagement;
